// src/pages/PublicProfilePage.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import ProductCard from "../components/ProductCard";
import Loader from "../components/Loader";
import StarRating from "../components/StarRating";
import { useNotification } from "../context/NotificationContext";
import { localizedProductName, localizedCategoryName } from "../utils/i18nContent";
import { 
  FiAlertCircle, 
  FiArrowLeft,
  FiShoppingBag,
  FiCheck,
  FiShare2,
  FiMessageCircle,
  FiShield,
  FiMapPin,
  FiSearch,
  FiX,
  FiSliders,
  FiChevronRight
} from "react-icons/fi";

const PublicProfilePage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { showNotification } = useNotification();

  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // In-store filter & search states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchSellerData = async () => {
      setLoading(true);
      setError("");
      try {
        const [sellerRes, productsRes] = await Promise.all([
          api.get(`/users/${id}/`),
          api.get(`/products/?supplier=${id}`),
        ]);

        if (!isMounted) return;

        if (sellerRes.data.user_type !== "SUPPLIER") {
          setError("This user is not an authorized supplier.");
        } else {
          setSeller(sellerRes.data);
          const rawProducts = productsRes.data.results || productsRes.data || [];
          setProducts(Array.isArray(rawProducts) ? rawProducts : []);
        }
      } catch (err) {
        if (isMounted) {
          setError("Could not connect to supplier profile.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSellerData();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Share store with Web Share API or clipboard fallback
  const handleShare = useCallback(async () => {
    const storeTitle =
      seller?.supplier_profile?.company_name ||
      `${seller?.first_name || ""} ${seller?.last_name || ""}`.trim() ||
      "Chafadi Seller";
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${storeTitle} | Chafadi`,
          text: `Check out ${storeTitle}'s store on Chafadi!`,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // Fallback to clipboard if share was dismissed or not allowed
        if (err.name === "AbortError") return;
      }
    }

    // Fallback: Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showNotification(t("publicProfilePage.storeLinkCopied"), "success");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      showNotification(shareUrl, "info");
    }
  }, [seller, showNotification, t]);

  // Extract unique categories from products
  const availableCategories = useMemo(() => {
    const map = new Map();
    products.forEach((prod) => {
      if (prod.category && prod.category.id) {
        const catId = String(prod.category.id);
        if (!map.has(catId)) {
          const catName = localizedCategoryName(prod.category, i18n.language) || prod.category.name || "Category";
          map.set(catId, { id: catId, name: catName, count: 1 });
        } else {
          map.get(catId).count += 1;
        }
      }
    });
    return Array.from(map.values());
  }, [products, i18n.language]);

  // Filtered and sorted products
  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];

    // 1. Search Query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((p) => {
        const name = localizedProductName(p, i18n.language).toLowerCase();
        const desc = (p.description || "").toLowerCase();
        const rawName = (p.name || "").toLowerCase();
        return name.includes(q) || desc.includes(q) || rawName.includes(q);
      });
    }

    // 2. Category filter
    if (selectedCategory !== "all") {
      result = result.filter((p) => String(p.category?.id) === String(selectedCategory));
    }

    // 3. Sorting
    result.sort((a, b) => {
      const priceA = parseFloat(a.price || a.min_price || a.max_price || 0);
      const priceB = parseFloat(b.price || b.min_price || b.max_price || 0);

      switch (sortBy) {
        case "price_asc":
          return priceA - priceB;
        case "price_desc":
          return priceB - priceA;
        case "rating":
          return (b.average_rating || 0) - (a.average_rating || 0);
        case "newest":
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

    return result;
  }, [products, searchQuery, selectedCategory, sortBy, i18n.language]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSortBy("newest");
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !seller) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
        <div className="bg-[var(--color-bg-secondary)] p-8 sm:p-12 rounded-[32px] border border-[var(--color-border)] flex flex-col items-center text-center gap-6 shadow-sm">
          <div className="w-16 h-16 bg-rose-500/10 text-rose-500 rounded-full flex items-center justify-center">
            <FiAlertCircle size={32} />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
              {error || "Seller profile not found"}
            </h2>
            <p className="text-sm text-[var(--color-text-secondary)]">
              The store you are looking for might have been moved, renamed, or is temporarily unavailable.
            </p>
          </div>
          <Link
            to="/"
            className="h-12 px-8 bg-[var(--color-accent)] hover:opacity-90 text-white rounded-full flex items-center font-bold text-sm shadow-md transition-all active:scale-95"
          >
            {t("publicProfilePage.returnHome")}
          </Link>
        </div>
      </div>
    );
  }

  const profile = seller.supplier_profile || {};
  const profileName =
    profile.company_name ||
    `${seller.first_name || ""} ${seller.last_name || ""}`.trim() ||
    "Chafadi Seller";
  const isVerified = Boolean(profile.is_verified || seller.is_verified || seller.is_staff);
  const memberYear = seller.date_joined ? new Date(seller.date_joined).getFullYear() : 2026;
  const ratingScore = seller.seller_average_rating > 0 ? Number(seller.seller_average_rating).toFixed(1) : "5.0";
  const sellerLocation = seller.country || "Niger";

  // Pick industry
  const primaryIndustry = profile.industries && profile.industries.length > 0 ? profile.industries[0] : null;
  const industryLabel = primaryIndustry
    ? (i18n.language === "fr" && primaryIndustry.name_fr ? primaryIndustry.name_fr : primaryIndustry.name_en || primaryIndustry.name)
    : null;

  const defaultBio = t("publicProfilePage.aboutDefault", { name: profileName });
  const bioText = profile.about?.trim() ? profile.about : defaultBio;

  const hasActiveFilters = searchQuery.trim() !== "" || selectedCategory !== "all" || sortBy !== "newest";

  return (
    <div className="min-h-screen bg-[var(--color-bg)] transition-colors duration-200">
      {/* ── 1. TOP MINIMAL NAVIGATION BAR ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs font-semibold text-[var(--color-text-secondary)]">
            <Link
              to="/"
              className="flex items-center gap-1.5 hover:text-[var(--color-accent)] transition-colors py-1"
            >
              <FiArrowLeft className="text-sm" />
              <span className="hidden sm:inline">Marketplace</span>
            </Link>
            <FiChevronRight className="text-[11px] opacity-40" />
            <span className="opacity-70">Stores</span>
            <FiChevronRight className="text-[11px] opacity-40" />
            <span className="text-[var(--color-text-primary)] font-bold truncate max-w-[140px] sm:max-w-[260px]">
              {profileName}
            </span>
          </div>

          <button
            onClick={handleShare}
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--color-bg-secondary)] hover:bg-[var(--color-border)]/40 border border-[var(--color-border)] text-xs font-bold text-[var(--color-text-primary)] transition-all active:scale-95 cursor-pointer"
            title={t("publicProfilePage.shareStore")}
          >
            {copied ? <FiCheck className="text-emerald-500 text-sm" /> : <FiShare2 className="text-sm text-[var(--color-accent)]" />}
            <span className="hidden sm:inline">{copied ? "Copied!" : t("publicProfilePage.shareStore")}</span>
          </button>
        </div>
      </div>

      {/* ── 2. SEAMLESS TOP AREA (NO BOXED CARD, CIRCULAR PIC, CENTERED NAME & BIO) ── */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-4 text-center flex flex-col items-center">
        
        {/* Circular Profile Picture + Verified Badge */}
        <div className="relative group shrink-0 mx-auto">
          <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full overflow-hidden bg-[var(--color-bg-secondary)] ring-4 ring-[var(--color-border)] shadow-md flex items-center justify-center">
            <img
              src={
                profile?.logo_url ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  profileName
                )}&background=ff6a00&color=fff&size=200&bold=true`
              }
              alt={profileName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>

          {isVerified && (
            <div
              className="absolute bottom-0 right-0 bg-emerald-500 text-white p-1.5 sm:p-2 rounded-full shadow-md ring-3 ring-[var(--color-bg)] flex items-center justify-center"
              title={t("publicProfilePage.verifiedSupplier")}
            >
              <FiCheck className="text-xs sm:text-sm font-black stroke-[3]" />
            </div>
          )}
        </div>

        {/* Store Name in Center */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[var(--color-text-primary)] tracking-tight mt-4 sm:mt-5">
          {profileName}
        </h1>

        {/* Seamless Metadata Row */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 text-xs text-[var(--color-text-secondary)] mt-2">
          {isVerified && (
            <span className="inline-flex items-center gap-1 font-bold text-emerald-600 dark:text-emerald-400">
              <FiShield className="text-xs" />
              {t("publicProfilePage.verifiedSupplier")}
            </span>
          )}

          <span className="opacity-30">•</span>

          <div className="flex items-center gap-1 font-bold text-[var(--color-text-primary)]">
            <StarRating rating={Number(ratingScore)} />
            <span className="text-[11px] font-black text-[var(--color-accent)] ml-0.5">
              {ratingScore}
            </span>
          </div>

          {industryLabel && (
            <>
              <span className="opacity-30">•</span>
              <span className="font-semibold text-[var(--color-text-primary)]">{industryLabel}</span>
            </>
          )}

          <span className="opacity-30">•</span>

          <span className="flex items-center gap-1 font-semibold">
            <FiMapPin className="text-[var(--color-accent)] text-xs" />
            {sellerLocation}
          </span>

          <span className="opacity-30 hidden sm:inline">•</span>

          <span className="font-medium hidden sm:inline">
            {t("publicProfilePage.memberSince", { year: memberYear })}
          </span>
        </div>

        {/* Bio Directly Under Store Name & Pic */}
        <p className="text-xs sm:text-sm md:text-base text-[var(--color-text-secondary)] leading-relaxed max-w-xl mx-auto mt-3 sm:mt-4 font-normal px-2">
          {bioText}
        </p>

        {/* Centered Action Buttons */}
        <div className="flex items-center justify-center gap-3 mt-5">
          <Link
            to={`/chat?user=${seller.id}`}
            className="inline-flex items-center gap-2 px-6 py-2.5 sm:py-3 rounded-full bg-[var(--color-accent)] hover:opacity-90 text-xs sm:text-sm font-bold text-white shadow-md shadow-[var(--color-accent)]/20 transition-all active:scale-95 cursor-pointer"
          >
            <FiMessageCircle className="text-sm sm:text-base" />
            <span>{t("publicProfilePage.contactSeller")}</span>
          </Link>

          <button
            onClick={handleShare}
            type="button"
            className="inline-flex items-center gap-2 px-5 py-2.5 sm:py-3 rounded-full bg-[var(--color-bg-secondary)] hover:bg-[var(--color-border)]/40 border border-[var(--color-border)] text-xs sm:text-sm font-bold text-[var(--color-text-primary)] transition-all active:scale-95 cursor-pointer"
            title={t("publicProfilePage.shareStore")}
          >
            {copied ? <FiCheck className="text-emerald-500 text-sm sm:text-base" /> : <FiShare2 className="text-sm sm:text-base text-[var(--color-accent)]" />}
            <span>{copied ? "Copied!" : t("publicProfilePage.shareStore")}</span>
          </button>
        </div>
      </div>

      {/* ── DIVIDER ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 my-6 sm:my-8 border-b border-[var(--color-border)]/60" />

      {/* ── 3. CATALOG SEARCH & CONTROLS ── */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 pb-28 sm:pb-36">
        {/* Catalog Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-[var(--color-text-primary)] tracking-tight">
              {t("publicProfilePage.storeCatalog")}
            </h2>
            <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] mt-0.5">
              {t("publicProfilePage.catalogSubtitle", { name: profileName })}
            </p>
          </div>

          {/* In-Store Search Bar */}
          <div className="flex items-center gap-2.5 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] text-sm pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("publicProfilePage.searchStorePlaceholder")}
                className="w-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full pl-9 pr-9 py-2 text-xs sm:text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:outline-none focus:border-[var(--color-accent)] transition-all shadow-inner"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] p-0.5 cursor-pointer"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            {/* Sort Selector */}
            <div className="relative shrink-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full pl-3.5 pr-8 py-2 text-xs sm:text-sm font-semibold text-[var(--color-text-primary)] cursor-pointer focus:outline-none focus:border-[var(--color-accent)] transition-colors shadow-sm"
              >
                <option value="newest">{t("publicProfilePage.sortNewest")}</option>
                <option value="price_asc">{t("publicProfilePage.sortPriceLow")}</option>
                <option value="price_desc">{t("publicProfilePage.sortPriceHigh")}</option>
                <option value="rating">{t("publicProfilePage.sortRating")}</option>
              </select>
              <FiSliders className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] pointer-events-none text-xs" />
            </div>
          </div>
        </div>

        {/* Category Pills Filter Bar */}
        {availableCategories.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 no-scrollbar">
            <button
              type="button"
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 active:scale-95 cursor-pointer ${
                selectedCategory === "all"
                  ? "bg-[var(--color-accent)] text-white shadow-sm shadow-[var(--color-accent)]/30"
                  : "bg-[var(--color-bg-secondary)] hover:bg-[var(--color-border)]/50 border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              }`}
            >
              {t("publicProfilePage.allCategories")} ({products.length})
            </button>

            {availableCategories.map((cat) => {
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 active:scale-95 cursor-pointer ${
                    isActive
                      ? "bg-[var(--color-accent)] text-white shadow-sm shadow-[var(--color-accent)]/30"
                      : "bg-[var(--color-bg-secondary)] hover:bg-[var(--color-border)]/50 border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                  }`}
                >
                  {cat.name} ({cat.count})
                </button>
              );
            })}
          </div>
        )}

        {/* Active Filter Pill Counter / Reset Action */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between bg-[var(--color-bg-secondary)] border border-[var(--color-border)] px-4 py-2 rounded-full mb-6 text-xs text-[var(--color-text-secondary)]">
            <span>
              {t("publicProfilePage.showingCount", {
                shown: filteredAndSortedProducts.length,
                total: products.length,
              })}
            </span>
            <button
              type="button"
              onClick={handleResetFilters}
              className="inline-flex items-center gap-1 font-bold text-[var(--color-accent)] hover:underline ml-3 cursor-pointer"
            >
              <FiX className="text-xs" />
              {t("publicProfilePage.clearFilters")}
            </button>
          </div>
        )}

        {/* ── 5. PRODUCT CATALOG GRID ── */}
        {filteredAndSortedProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5 sm:gap-4 md:gap-5">
            {filteredAndSortedProducts.map((product) => (
              <div key={product.id} className="animate-fade-in">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          /* Filtered empty state */
          <div className="py-14 sm:py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex items-center justify-center mx-auto mb-4 text-[var(--color-text-secondary)]">
              <FiSearch size={24} />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-[var(--color-text-primary)] mb-1.5">
              {t("publicProfilePage.noFilteredProducts")}
            </h3>
            <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] max-w-sm mx-auto mb-5">
              Try modifying your search keywords or switching category filters.
            </p>
            <button
              type="button"
              onClick={handleResetFilters}
              className="h-10 px-6 rounded-full bg-[var(--color-accent)] text-white text-xs font-bold shadow-md hover:opacity-90 transition-all active:scale-95 cursor-pointer"
            >
              {t("publicProfilePage.clearFilters")}
            </button>
          </div>
        ) : (
          /* Store completely empty */
          <div className="py-16 sm:py-24 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex items-center justify-center mx-auto mb-4 text-[var(--color-text-secondary)]/50">
              <FiShoppingBag size={28} />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-[var(--color-text-primary)] mb-1.5">
              {t("publicProfilePage.noProductsFromSeller")}
            </h3>
            <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] max-w-sm mx-auto mb-6">
              This seller hasn't listed any items in their store yet. Check back soon!
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-11 px-7 rounded-full bg-[var(--color-accent)] text-white text-xs font-bold shadow-md hover:opacity-90 transition-all active:scale-95"
            >
              <FiArrowLeft />
              <span>{t("publicProfilePage.returnHome")}</span>
            </Link>
          </div>
        )}

        {/* ── 6. IN-FLOW FOOTER REASSURANCE (Zero floating button overlap) ── */}
        <div className="mt-16 sm:mt-24 pt-8 border-t border-[var(--color-border)] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[var(--color-text-secondary)]">
          <div className="flex items-center gap-2">
            <FiShield className="text-[var(--color-accent)] text-sm" />
            <span>Trades protected by Chafadi Escrow & Inspection Guarantee</span>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-bold text-[var(--color-accent)] hover:underline"
          >
            <span>{t("publicProfilePage.returnHome")}</span>
            <FiChevronRight />
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PublicProfilePage;
